import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData, ColumnTypes } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SitesService } from 'src/app/sites/sites.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { Subscription } from 'rxjs';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import { DeviceFactory } from '../../device-factory/device-factory';

@Component({
	selector: 'app-exception-report-widget',
	templateUrl: './exception-report-widget.component.html',
	styleUrls: ['./exception-report-widget.component.css']
})
export class ExceptionReportWidgetComponent implements OnInit, OnChanges{
	@Input() widgetConfig: any;
	@Input() devices: any[] = [];
	@Input() data: any[] = [];
	@Input() filters: {
		selectedPeriod: string;
		selectedVoltage: string;
		selectedVintage: string[];
		selectedManufacturer: string[];
	} | any = null;

	isShownMap: boolean = false;
	siteId: number = 0;
	customerId: number = 0;

	deviceColor = DeviceFactory.createDevice(DeviceFactory.types.battlink).config.mapColor;

	// table data
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 20,
		draggable: true,
		fileName: '',
		csvFileHeaderInfo: []
	};

	columnConfig: ColumnsConfig[] = [];
	locations: any[] = [];
	siteSub: Subscription = new Subscription();
	siteInfo: any = {};

	constructor(
		public siteService: SitesService,
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private commonService: CommonService,
		private sideMenuService: SideMenuService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.tableConfig.fileName = this.widgetConfig.title;
		this.siteId = this.route?.parent?.snapshot?.params?.siteId || 0;
		this.customerId = this.route?.parent?.snapshot?.params?.customerId || 0;

		this.siteSub = this.sideMenuService.currentSite.subscribe((site) => {
			this.siteInfo = site;
			this.prepareColumnsNames();
		});
	}

	ngOnChanges() {
		this.updateChart();
	}

	prepareColumnsNames() {
		this.columnConfig = structuredClone(this.widgetConfig.staticColumnConfig);

		const extraFieldConfigs: { [key: string]: { type: ColumnTypes, hasTooltip?: string, noValueString?: string } } = {
			charger_utilization_score: { type: 'number' as ColumnTypes },
			charger_ah_utilization_score: { type: 'number' as ColumnTypes },
			charger_kwh_utilization_score: { type: 'number' as ColumnTypes },
			charger_kw_utilization: { type: 'number' as ColumnTypes },
			days_charger_disconnected: { type: 'number' as ColumnTypes },

			truck_utilization_score: { type: 'number' as ColumnTypes },
			truck_energy_usage_score: { type: 'number' as ColumnTypes },
			deep_discharge_cycles: { type: 'number' as ColumnTypes, hasTooltip: this.translateService.instant('exception_reports.deep_discharge_cycles_field_tooltip') },
			low_discharge_cycles: { type: 'number' as ColumnTypes, hasTooltip: this.translateService.instant('exception_reports.low_discharge_cycles_field_tooltip') },

			last_truck_location_long: { type: 'number' as ColumnTypes },
			last_truck_location_lat: { type: 'number' as ColumnTypes },
			last_charger_location_long: { type: 'number' as ColumnTypes },
			last_charger_location_lat: { type: 'number' as ColumnTypes },
			last_battery_location_long: { type: 'number' as ColumnTypes },
			last_battery_location_lat: { type: 'number' as ColumnTypes },

			days_outside_geofence: { type: 'number' as ColumnTypes, noValueString: this.translateService.instant('g.n/a') },
		};

		if (!this.widgetConfig?.extra_fields?.length)
			return;

		this.widgetConfig.extra_fields.forEach((key: string) => {
			const config = extraFieldConfigs[key];
			if (config) {
				this.columnConfig.push({
					...config,
					key,
					name: this.translateService.instant(`exception_reports.${key}`),
				});
			}
		});
	}

	updateChart() {
		const chartData = this.data[this.filters.selectedPeriod];
		
		this.tableData = this.devices.filter((device) => {
			const deviceData = device?.mac_address ? chartData?.[device.mac_address] || {} : {};

			if (this.widgetConfig.has_voltage_filter && this.filters.selectedVoltage !== 'all' && device.charger_voltage !== this.filters.selectedVoltage)
				return false;

			if (this.widgetConfig.has_vintage_filter && !this.siteDashboardService.isVintageWithinFilter(device, this.filters.selectedVintage))
				return false;

			if (this.widgetConfig.has_manufacturer_filter && this.filters.selectedManufacturer !== 'all' && device?.truck_manufacturer !== this.filters.selectedManufacturer)
				return false;

			if (this.widgetConfig.has_truck_type_filter && this.filters.selectedTruckType !== 'all' && device?.truck_type !== this.filters.selectedTruckType)
				return false;

			if (
				(
					this.widgetConfig?.extra_fields?.includes('last_truck_location_long') ||
					this.widgetConfig?.extra_fields?.includes('last_charger_location_long') ||
					this.widgetConfig?.extra_fields?.includes('last_battery_location_long')
				) && device?.is_far_device) {
				this.addLocation(device);
				return true;
			}

			return deviceData.showDevice;
		}).map((device) => {
			const data: { [key: string]: any } = {};

			const deviceData = device?.mac_address ? chartData?.[device.mac_address] || {} : {};

			for (const column of this.columnConfig) {
				data[column.key] = this.formatTableData(column, device, deviceData);
			}

			return data;
		});

		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			includeSpacing: false,
		});
	}

	addLocation(device: any) {
		this.locations.push({
			latitude: device.last_truck_location_lat ?? device.last_battery_location_lat ?? device.last_charger_location_lat,
			longitude: device.last_truck_location_long ?? device.last_battery_location_long ?? device.last_charger_location_long,
			type: 'device',
			truck_id: device.truck_id ?? device.charger_id,
			truck_sn: device.truck_sn ?? device.charger_sn,
			mac_address: device.mac_address,
			site_id: this.siteId,
			customer_id: this.customerId,
			serial_number: device.serial_number,
			sct_device_type: device?.sct_device_type,
			color: this.deviceColor,
			group: true,
		});
	}

	formatTableData(column: any, device: any, deviceData: any) {
		const res = {
			value: device[column.key] || 0,
			link:  ['/', this.customerId, this.siteId, device.mac_address],
		};

		switch (column.key) {
			case 'charger_utilization_score':
			case 'charger_ah_utilization_score':
			case 'charger_kwh_utilization_score':
			case 'charger_kw_utilization':
			case 'days_charger_disconnected':
			case 'truck_utilization_score':
			case 'truck_energy_usage_score':
				res.value = Math.round(deviceData?.value) || 0;
				break;
			case 'deep_discharge_cycles':
			case 'low_discharge_cycles':
				res.value = deviceData?.value || 0;
				break;
			case 'days_outside_geofence':
				if (device.is_estimated_location)
					res.value = null; // an estimated location has no GPS fix age - the column shows N/A (ST-2689)
				break;
			default:
				break;
		}

		return res;
	}

	ngOnDestroy() {
		if (this.siteSub)
			this.siteSub.unsubscribe();
	}
}
